import React, { useState } from 'react'
import Modal from 'aldoo-ra/Modal'
import Button from 'aldoo-ra/Button'
import Input from 'aldoo-ra/Input'
import Typography from 'aldoo-ra/Typography'
import { showNotification } from 'aldoo-ra/Notification'
import { SaveCMSContent, GetCMSContent } from 'aldoo-ra/CMS/api'
import { PlusCircleIcon } from '@heroicons/react/24/outline'

/**
 * AddNewPage Component
 * Simple dialog that asks for page name and creates an AldooPage with minimal configuration
 */
const AddNewPage = ({ isOpen, onClose, onPageCreated }) => {
  const [pageName, setPageName] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const handleCreate = async () => {
    if (!pageName.trim()) {
      showNotification({
        message: 'Please enter a page name',
        className: 'bg-admin_error text-white'
      })
      return
    }

    setIsSaving(true)

    try {
      // Check if page with this name already exists
      const existingResponse = await GetCMSContent({
        match: {
          'content.type': 'AldooPage',
          'content.name': pageName.trim()
        },
        itemsPerPage: 1
      })

      if (existingResponse.result && existingResponse.data && existingResponse.data.length > 0) {
        showNotification({
          message: `A page with name "${pageName}" already exists`,
          className: 'bg-admin_error text-white'
        })
        setIsSaving(false)
        return
      }

      // Create the page with minimal data
      const savePayload = {
        content: {
          type: 'AldooPage',
          name: pageName.trim(),
          status: 'draft',
          layout: [],
          link: []
        }
      }

      const response = await SaveCMSContent({ payload: savePayload })

      if (response.result) {
        showNotification({
          message: `Page "${pageName}" created successfully`,
          className: 'bg-admin_primary text-admin_text_inv'
        })

        // Reset form
        setPageName('')

        // Notify parent and close
        if (onPageCreated) {
          onPageCreated(response.data)
        }
        onClose()
      } else {
        showNotification({
          message: response.error || 'Failed to create page',
          className: 'bg-admin_error text-white'
        })
      }
    } catch (error) {
      console.error('Error creating page:', error)
      showNotification({
        message: 'An error occurred while creating the page',
        className: 'bg-admin_error text-white'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    setPageName('')
    onClose()
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSaving) {
      handleCreate()
    }
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Page"
      variant="admin"
      className="w-full md:w-[500px]"
      zIndex={200}
    >
      <div className="p-6 space-y-6">
        <div>
          <Typography variant="admin_label" className="mb-2">
            Page Name
          </Typography>
          <Input
            variant="admin"
            value={pageName}
            onChange={(e) => setPageName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter page name (e.g., About Us, Contact)"
            autoFocus
            disabled={isSaving}
          />
          <Typography variant="admin_txt1_dim" className="mt-2 text-xs">
            Page name must be unique
          </Typography>
        </div>

        <div className="flex justify-end pt-4 border-t border-admin_bordered">
          <Button
            variant="admin_primary"
            onClick={handleCreate}
            disabled={isSaving || !pageName.trim()}
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            {isSaving ? 'Creating...' : 'Create Page'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AddNewPage
